package com.snakesworld.game.network

import com.snakesworld.game.engine.LeaderboardEntry
import com.snakesworld.game.repository.LoggerRepository
import com.snakesworld.game.repository.LoggerRepositoryImpl
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.where
import kotlin.time.Clock
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlinx.coroutines.withTimeout
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.time.Duration.Companion.milliseconds

/**
 * Authoritative real-time multiplayer backend powered directly by Cloud Firestore.
 * Cloud Firestore is the absolute single source of truth for:
 * - Active snakes (collection: "world_snakes") - strictly real connected players, NO bots.
 * - Food and corpse pellets (collection: "world_pellets").
 * - Global leaderboards (collection: "leaderboards").
 * - User profiles (collection: "users").
 *
 * Document ids for users, leaderboards, and world snakes are the Firebase Auth uid.
 * No bots, single player simulations, or local fallbacks are used.
 */
class FirebaseMultiplayerRepository(
    private val scope: CoroutineScope = CoroutineScope(Dispatchers.Default),
    private val logger: LoggerRepository = LoggerRepositoryImpl(tag = "Firebase"),
) : MultiplayerRepository {

    private val _authState = MutableStateFlow<AuthState>(AuthState.Uninitialized)
    override val authState: StateFlow<AuthState> = _authState.asStateFlow()

    private val _connectionStatus = MutableStateFlow(ConnectionStatus.Disconnected)
    override val connectionStatus: StateFlow<ConnectionStatus> = _connectionStatus.asStateFlow()

    private val _snakes = MutableStateFlow<Map<String, SnakeNetworkState>>(emptyMap())
    override val snakes: StateFlow<Map<String, SnakeNetworkState>> = _snakes.asStateFlow()

    private val _pellets = MutableStateFlow<Map<String, PelletNetworkState>>(emptyMap())
    override val pellets: StateFlow<Map<String, PelletNetworkState>> = _pellets.asStateFlow()

    /** Firebase Auth uid; also the Firestore document id for users / leaderboards / world_snakes. */
    private var activeUserId: String = ""
    private var snakesListenerJob: Job? = null
    private var pelletsListenerJob: Job? = null

    override suspend fun initializeAndSignInAnonymously(): Result<String> {
        _authState.value = AuthState.Initializing
        return try {
            // Real time, not the test scheduler: virtual time would cancel sign-in immediately.
            val firebaseUid = withContext(Dispatchers.Default.limitedParallelism(1)) {
                withTimeout(12_000L.milliseconds) {
                    initPlatformFirebase()
                    val auth = Firebase.auth
                    val currentUser = auth.currentUser
                    val user = currentUser ?: auth.signInAnonymously().user
                    user?.uid ?: throw IllegalStateException("Firebase returned null anonymous user")
                }
            }
            activeUserId = firebaseUid
            _authState.value = AuthState.Authenticated(firebaseUid)
            Result.success(firebaseUid)
        } catch (e: Throwable) {
            val msg = e.message ?: "Authentication failed"
            _authState.value = AuthState.Error(msg)
            Result.failure(e)
        }
    }

    override suspend fun joinArena(profile: UserProfile, skinId: String): String {
        val authUser = Firebase.auth.currentUser ?: run {
            initializeAndSignInAnonymously()
            Firebase.auth.currentUser
        }
        val uid = authUser?.uid?.takeIf { it.isNotBlank() }
            ?: activeUserId.takeIf { !isEphemeralUserId(it) }
            ?: (authState.value as? AuthState.Authenticated)?.uid?.takeIf { !isEphemeralUserId(it) }
            ?: run {
                initializeAndSignInAnonymously().getOrNull()
                Firebase.auth.currentUser?.uid
            }
            ?: throw IllegalStateException("Firebase Auth uid is required to join the arena")

        activeUserId = uid
        _authState.value = AuthState.Authenticated(uid)

        val db = Firebase.firestore

        // Persist user profile to Firestore, preserving lifetime personal bests
        val existing = getUserProfile(uid)
        val merged = (existing ?: profile).copy(
            uid = uid,
            nickname = profile.nickname.ifBlank { existing?.nickname ?: "PLAYER_01" },
        )
        saveUserProfile(merged)

        // Authoritative real-time stream of all active player snakes from Firestore
        snakesListenerJob?.cancel()
        snakesListenerJob = scope.launch {
            while (isActive) {
                try {
                    db.collection("world_snakes")
                        .snapshots
                        .collect { querySnapshot ->
                            _connectionStatus.value = ConnectionStatus.Connected
                            val map = mutableMapOf<String, SnakeNetworkState>()
                            for (doc in querySnapshot.documents) {
                                try {
                                    val s = doc.data<SnakeNetworkState>()
                                    map[doc.id] = s
                                } catch (e: Throwable) {
                                    logger.e("decode SnakeNetworkState error for ${doc.id}: ${e::class.simpleName}: ${e.message}")
                                }
                            }
                            _snakes.value = map
                        }
                } catch (e: Throwable) {
                    logger.e("snakesListener error: ${e::class.simpleName}: ${e.message}")
                    _connectionStatus.value = ConnectionStatus.Unstable
                    delay(2000.milliseconds)
                }
            }
        }

        // Authoritative real-time stream of all food & corpse pellets from Firestore
        pelletsListenerJob?.cancel()
        pelletsListenerJob = scope.launch {
            while (isActive) {
                try {
                    db.collection("world_pellets")
                        .snapshots
                        .collect { querySnapshot ->
                            _connectionStatus.value = ConnectionStatus.Connected
                            val map = mutableMapOf<String, PelletNetworkState>()
                            for (doc in querySnapshot.documents) {
                                try {
                                    val p = doc.data<PelletNetworkState>()
                                    map[doc.id] = p
                                } catch (e: Throwable) {
                                    logger.e("decode PelletNetworkState error for ${doc.id}: ${e::class.simpleName}: ${e.message}")
                                }
                            }
                            _pellets.value = map
                        }
                } catch (e: Throwable) {
                    logger.e("pelletsListener error: ${e::class.simpleName}: ${e.message}")
                    _connectionStatus.value = ConnectionStatus.Unstable
                    delay(2000.milliseconds)
                }
            }
        }

        _connectionStatus.value = ConnectionStatus.Connected
        return uid
    }

    override suspend fun leaveArena() {
        snakesListenerJob?.cancel()
        pelletsListenerJob?.cancel()
        val uid = activeUserId
        if (uid.isNotEmpty()) {
            try {
                withTimeoutOrNull(1500L.milliseconds) {
                    Firebase.firestore.collection("world_snakes").document(uid).delete()
                }
            } catch (_: Throwable) {}
        }
        _snakes.value = emptyMap()
        _pellets.value = emptyMap()
        _connectionStatus.value = ConnectionStatus.Disconnected
    }

    override suspend fun broadcastHeadState(state: SnakeNetworkState) {
        val effectiveState = if (state.id.startsWith("pilot_") && activeUserId.isNotEmpty() && !activeUserId.startsWith("pilot_")) {
            state.copy(id = activeUserId)
        } else {
            state
        }
        try {
            Firebase.firestore.collection("world_snakes").document(effectiveState.id).set(effectiveState)
            if (_connectionStatus.value != ConnectionStatus.Connected) {
                _connectionStatus.value = ConnectionStatus.Connected
            }
        } catch (e: Throwable) {
            logger.e("broadcastHeadState error: ${e::class.simpleName}: ${e.message}")
            _connectionStatus.value = ConnectionStatus.Unstable
        }
    }

    override suspend fun spawnPellets(pellets: List<PelletNetworkState>) {
        if (pellets.isEmpty()) return
        try {
            val db = Firebase.firestore
            for (chunk in pellets.chunked(100)) {
                val batch = db.batch()
                for (p in chunk) {
                    val ref = db.collection("world_pellets").document(p.id)
                    batch.set(ref, p)
                }
                batch.commit()
            }
            if (_connectionStatus.value != ConnectionStatus.Connected) {
                _connectionStatus.value = ConnectionStatus.Connected
            }
        } catch (e: Throwable) {
            logger.e("spawnPellets error: ${e::class.simpleName}: ${e.message}")
            _connectionStatus.value = ConnectionStatus.Unstable
        }
    }

    override suspend fun consumePellets(pelletIds: List<String>) {
        if (pelletIds.isEmpty()) return
        try {
            val db = Firebase.firestore
            for (chunk in pelletIds.chunked(100)) {
                val batch = db.batch()
                for (id in chunk) {
                    val ref = db.collection("world_pellets").document(id)
                    batch.delete(ref)
                }
                batch.commit()
            }
            if (_connectionStatus.value != ConnectionStatus.Connected) {
                _connectionStatus.value = ConnectionStatus.Connected
            }
        } catch (e: Throwable) {
            logger.e("consumePellets error: ${e::class.simpleName}: ${e.message}")
            _connectionStatus.value = ConnectionStatus.Unstable
        }
    }

    override suspend fun reportPlayer(targetId: String, targetName: String) {
        val uid = activeUserId.takeIf { it.isNotBlank() && !isEphemeralUserId(it) }
            ?: (authState.value as? AuthState.Authenticated)?.uid?.takeIf { !isEphemeralUserId(it) }
            ?: return
        if (targetId.isBlank() || targetId == uid) return
        val safeName = targetName.trim().take(32).ifBlank { return }
        try {
            val report = PlayerReport(
                reporterId = uid,
                targetId = targetId,
                targetName = safeName,
                createdAt = Clock.System.now().toEpochMilliseconds(),
            )
            Firebase.firestore.collection("reports").document("${uid}_$targetId").set(report)
        } catch (e: Throwable) {
            logger.e("reportPlayer error: ${e::class.simpleName}: ${e.message}")
        }
    }

    override suspend fun submitDemise(victimId: String, deathDrops: List<PelletNetworkState>) {
        val effectiveVictimId = if (victimId.startsWith("pilot_") && activeUserId.isNotEmpty() && !activeUserId.startsWith("pilot_")) {
            activeUserId
        } else {
            victimId
        }
        try {
            val db = Firebase.firestore
            val batch = db.batch()
            val victimRef = db.collection("world_snakes").document(effectiveVictimId)
            batch.delete(victimRef)
            for (orb in deathDrops.take(100)) {
                val orbRef = db.collection("world_pellets").document(orb.id)
                batch.set(orbRef, orb)
            }
            batch.commit()
            if (_connectionStatus.value != ConnectionStatus.Connected) {
                _connectionStatus.value = ConnectionStatus.Connected
            }
        } catch (e: Throwable) {
            logger.e("submitDemise error: ${e::class.simpleName}: ${e.message}")
            _connectionStatus.value = ConnectionStatus.Unstable
        }
    }

    override suspend fun getLeaderboard(): List<LeaderboardEntry> {
        return try {
            val db = Firebase.firestore
            val query = db.collection("leaderboards").get()
            val list = query.documents.mapNotNull { doc ->
                try { doc.data<LeaderboardEntry>() } catch (_: Throwable) { null }
            }
            list.sortedByDescending { it.score }
        } catch (_: Throwable) {
            emptyList()
        }
    }


    override suspend fun recordGameStarted(isSinglePlayer: Boolean) {
        val uid = activeUserId.takeIf { it.isNotBlank() && !isEphemeralUserId(it) }
            ?: (authState.value as? AuthState.Authenticated)?.uid?.takeIf { it.isNotBlank() }
            ?: try { Firebase.auth.currentUser?.uid?.takeIf { it.isNotBlank() } } catch (_: Throwable) { null }
            ?: return
        activeUserId = uid
        try {
            val profile = getUserProfile(uid) ?: UserProfile(
                uid = uid,
                nickname = "PLAYER_${uid.takeLast(4)}",
            )
            val updated = if (isSinglePlayer) {
                profile.copy(uid = uid, spGamesPlayed = profile.spGamesPlayed + 1)
            } else {
                profile.copy(uid = uid, gamesPlayed = profile.gamesPlayed + 1)
            }
            saveUserProfile(updated)
        } catch (e: Throwable) {
            logger.e("recordGameStarted error: ${e::class.simpleName}: ${e.message}")
        }
    }

    override suspend fun submitHighScore(
        score: Int,
        kills: Int,
        skinId: String,
        length: Int,
        isSinglePlayer: Boolean,
    ) {
        val uid = activeUserId.takeIf { it.isNotBlank() && !isEphemeralUserId(it) }
            ?: (authState.value as? AuthState.Authenticated)?.uid?.takeIf { it.isNotBlank() }
            ?: try { Firebase.auth.currentUser?.uid?.takeIf { it.isNotBlank() } } catch (_: Throwable) { null }
            ?: return
        activeUserId = uid
        try {
            val profile = getUserProfile(uid) ?: UserProfile(
                uid = uid,
                nickname = "PLAYER_${uid.takeLast(4)}",
            )
            val updatedProfile = if (isSinglePlayer) {
                profile.copy(
                    uid = uid,
                    spBestScore = maxOf(profile.spBestScore, score),
                    spBestKills = maxOf(profile.spBestKills, kills),
                    spBestLength = maxOf(profile.spBestLength, length),
                )
            } else {
                profile.copy(
                    uid = uid,
                    bestScore = maxOf(profile.bestScore, score),
                    bestKills = maxOf(profile.bestKills, kills),
                    bestLength = maxOf(profile.bestLength, length),
                )
            }
            saveUserProfile(updatedProfile)

            // Public leaderboard is multiplayer-only.
            if (!isSinglePlayer) {
                val entry = LeaderboardEntry(
                    id = uid,
                    name = updatedProfile.nickname,
                    score = updatedProfile.bestScore,
                    kills = updatedProfile.bestKills,
                    skinId = skinId,
                )
                Firebase.firestore.collection("leaderboards").document(uid).set(entry)
            }
        } catch (e: Throwable) {
            logger.e("submitHighScore error: ${e::class.simpleName}: ${e.message}")
        }
    }

    override suspend fun getUserProfile(uid: String): UserProfile? {
        if (uid.isEmpty()) return null
        return try {
            withTimeoutOrNull(1500L.milliseconds) {
                val doc = Firebase.firestore.collection("users").document(uid).get()
                if (doc.exists) doc.data<UserProfile>() else null
            }
        } catch (_: Throwable) {
            null
        }
    }

    override suspend fun saveUserProfile(profile: UserProfile) {
        // Never recreate a profile that was erased or has no uid.
        if (profile.uid.isEmpty() || profile.markDelete) return
        try {
            withTimeoutOrNull(1500L.milliseconds) {
                Firebase.firestore.collection("users").document(profile.uid).set(profile)
            }
        } catch (_: Throwable) {}
    }

    override suspend fun signOut(): Result<Unit> {
        return try {
            snakesListenerJob?.cancel()
            snakesListenerJob = null
            pelletsListenerJob?.cancel()
            pelletsListenerJob = null
            _connectionStatus.value = ConnectionStatus.Disconnected
            _snakes.value = emptyMap()
            _pellets.value = emptyMap()
            activeUserId = ""
            val auth = Firebase.auth
            try {
                withTimeout(5000L.milliseconds) {
                    auth.currentUser?.delete()
                }
            } catch (e: Throwable) {
                logger.e("auth.currentUser.delete error: ${e::class.simpleName}: ${e.message}, attempting auth.signOut()")
                try {
                    withTimeout(5000L.milliseconds) {
                        auth.signOut()
                    }
                } catch (_: Throwable) {}
            }
            _authState.value = AuthState.Uninitialized
            Result.success(Unit)
        } catch (e: Throwable) {
            logger.e("signOut error: ${e::class.simpleName}: ${e.message}")
            _authState.value = AuthState.Uninitialized
            Result.failure(e)
        }
    }

    override suspend fun clearUserData(userId: String): Result<Unit> {
        val targetUid = try { Firebase.auth.currentUser?.uid?.takeIf { it.isNotBlank() } } catch (_: Throwable) { null }
            ?: userId.takeIf { !isEphemeralUserId(it) }
            ?: activeUserId.takeIf { !isEphemeralUserId(it) }
            ?: (authState.value as? AuthState.Authenticated)?.uid?.takeIf { !isEphemeralUserId(it) }
        if (targetUid == null) {
            return Result.failure(IllegalStateException("No active user ID to clear"))
        }

        return try {
            val db = Firebase.firestore
            val userRef = db.collection("users").document(targetUid)

            // Step 1: Mark the user document while auth/UID are still valid (best-effort).
            // Never fail the erase on this — live rules may reject unknown fields.
            try {
                withTimeout(4000L.milliseconds) {
                    userRef.updateFields {
                        "markDelete" to true
                    }
                }
                logger.i("Successfully marked /users/$targetUid with markDelete=true")
            } catch (e: Throwable) {
                logger.e("Update markDelete failed or doc missing: ${e::class.simpleName}: ${e.message}")
            }

            // Step 2: Delete /users/{uid} ALONE before sign-out.
            // Do NOT batch with world_snakes/leaderboards — live rules can deny those and
            // would roll back the user delete (PERMISSION_DENIED on the whole batch).
            withTimeout(6000L.milliseconds) {
                userRef.delete()
            }
            logger.i("Deleted /users/$targetUid")

            suspend fun userDocExists(): Boolean {
                return try {
                    withTimeout(4000L.milliseconds) {
                        userRef.get().exists
                    }
                } catch (e: Throwable) {
                    logger.e("verify get /users/$targetUid failed: ${e::class.simpleName}: ${e.message}")
                    true
                }
            }

            if (userDocExists()) {
                logger.d("/users/$targetUid still present; retrying direct delete")
                withTimeout(6000L.milliseconds) {
                    userRef.delete()
                }
            }
            if (userDocExists()) {
                return Result.failure(
                    IllegalStateException("Failed to delete Firestore user document /users/$targetUid")
                )
            }
            logger.i("Verified /users/$targetUid is deleted")

            // Best-effort related cleanup — failures must not undo the user delete.
            try {
                withTimeout(3000L.milliseconds) {
                    db.collection("leaderboards").document(targetUid).delete()
                }
                logger.i("Deleted /leaderboards/$targetUid")
            } catch (e: Throwable) {
                logger.e("Error deleting /leaderboards/$targetUid: ${e::class.simpleName}: ${e.message}")
            }
            try {
                withTimeout(3000L.milliseconds) {
                    db.collection("world_snakes").document(targetUid).delete()
                }
                logger.i("Deleted /world_snakes/$targetUid")
            } catch (e: Throwable) {
                logger.e("Error deleting /world_snakes/$targetUid: ${e::class.simpleName}: ${e.message}")
            }
            try {
                withTimeout(4000L.milliseconds) {
                    val mine = db.collection("reports").where { "reporterId" equalTo targetUid }.get()
                    for (doc in mine.documents) {
                        db.collection("reports").document(doc.id).delete()
                    }
                }
            } catch (e: Throwable) {
                logger.e("Error deleting reports for $targetUid: ${e::class.simpleName}: ${e.message}")
            }

            // Step 3: Delay, then sign out only after the user document delete is verified.
            delay(1000L.milliseconds)
            val signOutResult = signOut()
            if (signOutResult.isFailure) {
                return signOutResult
            }
            Result.success(Unit)
        } catch (e: Throwable) {
            logger.e("clearUserData error: ${e::class.simpleName}: ${e.message}")
            Result.failure(e)
        }
    }
}
